import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Database, ShieldAlert, Fingerprint } from 'lucide-react';
import useOSINTTotals from '../../hooks/useOSINTTotals';
import { OSINTTotalsResponse } from '../../api/countService';
import Loader from '../Loader';
import { useTranslation } from 'react-i18next';

interface OSINTItem {
  type: keyof OSINTTotalsResponse;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  colorClass: string;
}

const osintMapping: OSINTItem[] = [
  { type: 'dehashed_count', icon: Database, colorClass: 'text-blue-500' },
  { type: 'ip_vulnerabilities_count', icon: ShieldAlert, colorClass: 'text-red-500' },
  { type: 'typosquatting_names_count', icon: Fingerprint, colorClass: 'text-yellow-400' },
];

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(num);
};

const VulnerabilitiesSummary: React.FC = () => {
  const { totals, loading, error } = useOSINTTotals();
  const { t } = useTranslation();

  if (loading) return <Loader />;
  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!totals) return <p className="text-center text-gray-500">No hay datos disponibles.</p>;

  const osintData = osintMapping.map((item) => ({
    ...item,
    count: totals[item.type] || 0,
  }));

  return (
    <div>
      {/* Título con color más suave */}
      <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-10 mt-6 text-white">
        {t('charts.vulnerability_summary.title')}
      </h2>

      {/* Tarjetas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {osintData.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <Card
              key={index}
              className="bg-[#0f172a] border border-gray-600 shadow-lg rounded-lg p-6 text-center"
            >
              <CardHeader>
                <div className={`flex justify-center mb-4 ${item.colorClass}`}>
                  <IconComponent className="h-14 w-14 scale-125" />
                </div>
                <CardTitle className="text-5xl font-extrabold text-white">
                  {formatNumber(item.count)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-semibold text-gray-400">
                  {t(`charts.vulnerability_summary.${item.type}`)}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default VulnerabilitiesSummary;
